import { ContentUnit } from "@/features/content/ContentUnit";
import {
  ImageSizeOverrideProvider,
  useQuestionImageSizeOverrideValue,
} from "@/features/content/imageSizeOverrideContext";
import type { HeadingLevel } from "@/features/content/TextUnit";
import type { NormalizedContentUnit } from "@/features/content/useComponent";
import type { UserRole } from "@/features/courses/useCourse";

import { processQuestionText } from "./questionTextProcessing";
import styles from "./QuestionSection.module.css";

interface QuestionContentProps {
  headingLevel?: HeadingLevel;
  role?: UserRole;
  units: NormalizedContentUnit[];
}

export function QuestionContent({
  headingLevel = 4,
  role = "student",
  units,
}: QuestionContentProps) {
  // The question boundary: a course-wide image override reaches question images
  // only when the instructor asked for it, and this is where that is decided.
  // Hooks run before the early return below.
  const imageSizeOverride = useQuestionImageSizeOverrideValue();

  if (units.length === 0) {
    return null;
  }

  return (
    <ImageSizeOverrideProvider value={imageSizeOverride}>
      <div className={styles.contentStack}>
        {units.map((unit) => {
          if (unit.kind !== "text") {
            return (
              <ContentUnit
                headingLevel={headingLevel}
                key={unit.id}
                unit={unit}
              />
            );
          }

          const processed = processQuestionText(unit.html, role);
          const visibleUnit = {
            ...unit,
            html: processed.studentText,
          };

          return (
            <div className={styles.processedContentUnit} key={unit.id}>
              <ContentUnit headingLevel={headingLevel} unit={visibleUnit} />
              {processed.instructorComments.length > 0 ? (
                <div className={styles.instructorComments}>
                  {processed.instructorComments.map((comment, index) => (
                    <p
                      className={styles.instructorComment}
                      key={`${unit.id}-comment-${index}`}
                    >
                      <span className={styles.instructorCommentLabel}>
                        Instructor note
                      </span>
                      {comment}
                    </p>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </ImageSizeOverrideProvider>
  );
}
